import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

const GAP = 8
const MARGIN = 8

interface Props {
  anchor: RefObject<HTMLElement | null>
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Preferred side; flips when there is not enough room. */
  side?: 'top' | 'bottom'
  align?: 'start' | 'center' | 'end'
  className?: string
  label?: string
}

/**
 * Popover renders above everything else, positioned next to its anchor and
 * kept inside the viewport. In fullscreen it renders into the fullscreen
 * element, the only part of the page that is visible then.
 */
export function Popover({ anchor, open, onClose, children, side = 'top', align = 'end', className = '', label }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const place = () => {
      const a = anchor.current?.getBoundingClientRect()
      const el = ref.current
      if (!a || !el) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      const w = el.offsetWidth
      const h = el.scrollHeight
      const above = a.top - GAP - MARGIN
      const below = vh - a.bottom - GAP - MARGIN
      const useTop = side === 'top' ? above >= Math.min(h, 240) || above >= below : below < Math.min(h, 240) && above > below
      const maxHeight = Math.max(120, useTop ? above : below)
      const height = Math.min(h, maxHeight)
      let left = align === 'start' ? a.left : align === 'center' ? a.left + a.width / 2 - w / 2 : a.right - w
      left = Math.min(Math.max(MARGIN, left), vw - w - MARGIN)
      const top = useTop ? a.top - GAP - height : a.bottom + GAP
      setPos({ left, top, maxHeight })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, anchor, side, align, children])

  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || anchor.current?.contains(t)) return
      // Things adjusted while a menu is open (dragged subtitles) keep it open.
      if (t instanceof Element && t.closest('[data-keeps-popover]')) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        anchor.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointer, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose, anchor])

  if (!open) return null
  const container = document.fullscreenElement ?? document.body
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      className={`popover ${className}`}
      style={
        pos
          ? { left: pos.left, top: pos.top, maxHeight: pos.maxHeight }
          : // Measure off screen before the first placement.
            { left: -9999, top: 0 }
      }
    >
      {children}
    </div>,
    container,
  )
}
